import React from "react";
import { Avatar, Button, Flex, Spin } from "antd";
import {
  isHTML,
  roleType,
  YesOrNoEnmu,
  siteServiceLogo,
  FORMATS_DATE,
} from "@/constants";
import { downloadFile, getSiteTheme } from "@/utils";
import {
  ExclamationOutlined,
  LoadingOutlined,
  PaperClipOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { useWebSocket } from "../WebSocketProvider";
import LikeComponent from "../LikeComponent";
import ComponentRenderer from "../ComponentRenderer";
import styles from "./index.module.scss";

interface ConversationContentProps {
  messages: any[]; //消息列表数据
  dialogueContentRef: React.RefObject<HTMLDivElement>;
  onRetry?: (messageId: string) => void; // 添加重试回调
  setDialogueMessages?: any;
  handleSendMessage: (values: any) => void; //发送消息方法
  privacy?: string;
}

//对话内容
const ConversationContent = ({
  messages,
  dialogueContentRef,
  onRetry,
  setDialogueMessages,
  handleSendMessage,
  privacy,
}: ConversationContentProps) => {
  const { error } = useWebSocket();

  //客服头像
  const avatar = {
    [roleType.cs]: `${process.env.REACT_APP_AGENT_URL}/images/default.jpg`,
    [roleType.assistant]:
      process.env.REACT_APP_AGENT_URL +
      siteServiceLogo[getSiteTheme(window.location.host)].aiAvatar,
  };

  //获取support路径地址
  const supportUrl =
    process.env.REACT_APP_ENV === "production" ||
    process.env.REACT_APP_ENV === "stage"
      ? process.env.REACT_APP_SUPPORT_API
      : "https://integrated-test.afirstsoft.cn";

  //下载文件
  const handleDownLoadFile = async (file: any) => {
    if (file) {
      //下载附件的时候需要自己拼接下载的路径
      const resultPath = `${supportUrl}/api/v1/attachment?name=${file.name}&path=${file.path}`;
      const { data } = await axios.get(resultPath, {
        responseType: "blob",
      });
      downloadFile(data, file.name);
    }
  };

  //附件信息渲染
  const filesRender = (id: any, attachments: any[]) => {
    return attachments?.map((file: any, index: number) => (
      <Button
        type="link"
        key={`file-${id}-${index}`}
        style={{
          display: "inline-block",
          padding: 0,
          height: "auto",
          whiteSpace: "pre-wrap",
          textAlign: "start",
        }}
        onClick={async (event) => {
          event.preventDefault();
          event.stopPropagation();
          handleDownLoadFile(file);
        }}
      >
        <PaperClipOutlined />
        <span>{file.name}</span>
      </Button>
    ));
  };

  return (
    <div id={styles.outputWindowWrap} ref={dialogueContentRef}>
      <ul id={styles.dialogueBox}>
        {privacy && (
          <li className={`${styles.dialogueItem} ${styles.privacyContent}`}>
            {(() => {
              const parts = privacy.split("\n");
              const title = parts[0];
              const content = parts.slice(1).join("\n");
              return (
                <>
                  {title && <div className={styles.privacyTitle}>{title}</div>}
                  {content && (
                    <div
                      className={styles.privacyBody}
                      dangerouslySetInnerHTML={{
                        __html: content.replace(/\n/g, "<br />"),
                      }}
                    />
                  )}
                </>
              );
            })()}
          </li>
        )}
        {messages?.map((item: any) => {
          const isAssistant = [roleType.assistant, roleType.cs].includes(
            item.role,
          );
          return (
            <React.Fragment key={`message-fragment-${item.id}`}>
              <li
                key={`message-${item.id}`}
                className={`${styles.dialogueItem} ${
                  isAssistant
                    ? styles.dialogueItemLeft
                    : styles.dialogueItemRight
                }`}
              >
                {item.role === roleType.user ? (
                  <>
                    {item?.attachments && item?.attachments?.length > 0 && (
                      <Flex vertical={true} align="center">
                        <div className={styles.dialogueTime}>
                          {dayjs(item.create_time * 1000).format(
                            FORMATS_DATE.DATETIME,
                          )}
                        </div>
                        {item?.attachments && item?.attachments?.length > 0 && (
                          <div className={styles.content}>
                            {filesRender(item.id, item.attachments)}
                          </div>
                        )}
                      </Flex>
                    )}
                    {item.message && (
                      <Flex vertical={true} align="center">
                        <div className={styles.dialogueTime}>
                          {dayjs(item.create_time * 1000).format(
                            FORMATS_DATE.DATETIME,
                          )}
                        </div>
                        <Flex gap={8} style={{ alignSelf: "flex-end" }}>
                          {/* 网络异常显示重试图标 */}
                          {item.needRetry && !item.retryLoading && (
                            <div className={styles.retryIcon}>
                              <ExclamationOutlined
                                onClick={() => onRetry?.(item.id)}
                              />
                            </div>
                          )}
                          {/* 显示重试加载状态 */}
                          {item.retryLoading && <LoadingOutlined />}
                          <div className={styles.content}>{item.message}</div>
                        </Flex>
                      </Flex>
                    )}
                  </>
                ) : (
                  <>
                    {/* 附件显示 */}
                    {item?.attachments && item?.attachments?.length > 0 && (
                      <Flex align="center" style={{ marginBottom: 16 }}>
                        <Avatar
                          size={32}
                          style={{
                            marginInlineEnd: 8,
                            alignSelf: "flex-start",
                            flexShrink: 0,
                          }}
                          src={<img src={avatar[item.role]} alt="avatar" />}
                        />
                        <div className={styles.content}>
                          {filesRender(item.id, item.attachments)}
                        </div>
                      </Flex>
                    )}
                    {/* 信息显示 */}
                    {item.message && (
                      <Flex align="center">
                        <Avatar
                          size={32}
                          style={{
                            marginInlineEnd: 8,
                            alignSelf: "flex-start",
                            flexShrink: 0,
                          }}
                          src={<img src={avatar[item.role]} alt="avatar" />}
                        />
                        {isHTML(item.message) ? (
                          <div
                            className={`${styles.content} ${styles.isHtml}`}
                            dangerouslySetInnerHTML={{
                              __html: item.message,
                            }}
                          />
                        ) : (
                          <div className={styles.content}>{item.message}</div>
                        )}
                      </Flex>
                    )}
                  </>
                )}
                {/* 智能体回答显示点赞功能 isShowAction为自己添加标识的参数， 在sse请求结束的时候在添加*/}
                {isAssistant &&
                  item.message &&
                  item.find_answer === YesOrNoEnmu.YES &&
                  item?.isShowAction && <LikeComponent message={item} />}
              </li>
              {/* 显示loading */}
              {item?.loading && (
                <li
                  className={`${styles.dialogueItem} ${styles.loadingText}`}
                  key={`loading-${item.id}`}
                >
                  <Spin />
                </li>
              )}
              {/* 渲染tips提示 */}
              {item.tips && !item?.hasTickFormSuccess && (
                <li
                  key={`tip-${item.id}`}
                  className={`${styles.dialogueItem} ${styles.tootipContent}`}
                >
                  {item.tips}
                </li>
              )}
              {/* 组件渲染 */}
              {item.component && (
                <ComponentRenderer
                  message={item}
                  setDialogueMessages={setDialogueMessages}
                  handleSendMessage={handleSendMessage}
                />
              )}
            </React.Fragment>
          );
        })}
        {/* 如果有错误信息则提示 */}
        {error && (
          <li
            className={`${styles.dialogueItem} ${styles.tootipContent}`}
            key={"error"}
          >
            {error}
          </li>
        )}
      </ul>
    </div>
  );
};

export default ConversationContent;
